import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { TablePricingManagerContextColumn } from 'lumen/components/hotel/pricing-assistant/table/table-pricing-manager';
import type { Period } from 'lumen/models';
import { calculateTotalRoomsForSale } from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';

interface HotelPricingAssistantTableCellsTdHotelLeftToSellMonthSignature {
  Args: {
    column: TablePricingManagerContextColumn;
    row: { period: Period };
  };
}

export default class HotelPricingAssistantTableCellsTdHotelLeftToSellMonth extends Component<HotelPricingAssistantTableCellsTdHotelLeftToSellMonthSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  get displayValue(): number | string {
    return this.roomsForSale ?? this.intl.t('general.missing');
  }

  get isLoading(): boolean {
    return this.args.row.period.hasOverbookingActionInProgress;
  }

  get isMissing(): boolean {
    return this.roomsForSale == null;
  }

  get isPast(): boolean {
    return this.hotelAndMarketService.isPast(this.args.row.period.fromDate);
  }

  get roomsForSale(): number | null {
    const { period } = this.args.row;
    const availableRooms = period.day.occupancy?.availableRooms;
    const overbooking = period.effectiveOverbookingWithOverride;

    if (availableRooms == null || overbooking == null) {
      return null;
    }

    return calculateTotalRoomsForSale(availableRooms, overbooking);
  }

  <template>
    {{#if this.isLoading}}
      <div class="loading-pulse" data-test-id="loading-pulse"></div>
    {{else}}
      <div
        class="mr-500
          {{if
            this.isPast
            'text-disabled'
            (if this.isMissing 'text-neutral-subdued')
          }}"
        data-test-id="td-hotel-left-to-sell-month"
      >
        {{this.displayValue}}
      </div>
    {{/if}}
  </template>
}
